import { debugLog } from '../utils/debugLog';
/**
 * Client-Side Rate Limiting Manager
 * Implements industry-standard rate limiting for authentication attempts
 */

interface RateLimitAttempt {
  count: number;
  lastAttempt: number;
  firstAttempt: number;
}


interface RateLimitResult {
  allowed: boolean;
  attemptsRemaining: number;
  resetTime?: number; // When the limit resets
  message?: string;
}

type RateLimitOperation = 'login' | 'registration' | 'passwordReset' | 'emailVerification';

export class RateLimitManager {
  private attempts = new Map<string, RateLimitAttempt>();
  private readonly cleanupInterval: NodeJS.Timeout;

  // Default configurations for different operations
  private readonly configs = {
    login: {
      maxAttempts: 5,
      windowMs: 15 * 60 * 1000, // 15 minutes
      blockDurationMs: 15 * 60 * 1000 // 15 minutes
    },
    registration: {
      maxAttempts: 3,
      windowMs: 60 * 60 * 1000, // 1 hour
      blockDurationMs: 60 * 60 * 1000 // 1 hour
    },
    passwordReset: {
      maxAttempts: 3,
      windowMs: 60 * 60 * 1000, // 1 hour
      blockDurationMs: 60 * 60 * 1000 // 1 hour
    },
    emailVerification: {
      maxAttempts: 5,
      windowMs: 60 * 60 * 1000, // 1 hour
      blockDurationMs: 30 * 60 * 1000 // 30 minutes
    }
  };

  constructor() {
    // Clean up old attempts every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000);
  }

  /**
   * Check if an operation is allowed for a given identifier
   */
  checkRateLimit(
    identifier: string, 
    operation: RateLimitOperation
  ): RateLimitResult {
    const config = this.configs[operation];
    const key = `${operation}:${identifier.toLowerCase()}`;
    const now = Date.now();
    
    const attempt = this.attempts.get(key);

    // No previous attempts
    if (!attempt) {
      this.attempts.set(key, {
        count: 1,
        lastAttempt: now,
        firstAttempt: now
      });
      
      return {
        allowed: true,
        attemptsRemaining: config.maxAttempts - 1
      };
    }

    // Block check MUST come before the window reset. With the reset first, a
    // blocked user whose attempt window expired (windowMs is shorter than
    // blockDurationMs) had their lockout cut short and could keep guessing.
    const isBlocked =
      attempt.count >= config.maxAttempts &&
      now - attempt.lastAttempt < config.blockDurationMs;

    // Check if the time window has expired
    if (!isBlocked && now - attempt.firstAttempt > config.windowMs) {
      // Reset the window
      this.attempts.set(key, {
        count: 1,
        lastAttempt: now,
        firstAttempt: now
      });
      
      return {
        allowed: true,
        attemptsRemaining: config.maxAttempts - 1
      };
    }

    // Check if still in block period
    if (attempt.count >= config.maxAttempts) {
      const timeSinceLastAttempt = now - attempt.lastAttempt;
      
      if (timeSinceLastAttempt < config.blockDurationMs) {
        const resetTime = attempt.lastAttempt + config.blockDurationMs;
        const remainingMs = resetTime - now;
        const remainingMinutes = Math.ceil(remainingMs / (60 * 1000));
        
        return {
          allowed: false,
          attemptsRemaining: 0,
          resetTime,
          message: `Too many attempts. Please wait ${remainingMinutes} minute(s) before trying again.`
        };
      } else {
        // Block period has expired, reset
        this.attempts.set(key, {
          count: 1,
          lastAttempt: now,
          firstAttempt: now
        });
        
        return {
          allowed: true,
          attemptsRemaining: config.maxAttempts - 1
        };
      }
    }

    // Increment attempt count
    attempt.count++;
    attempt.lastAttempt = now;
    this.attempts.set(key, attempt);

    const remaining = config.maxAttempts - attempt.count;
    
    return {
      allowed: true,
      attemptsRemaining: remaining,
      // Login warns the user at <= 2 remaining, so the message has to be
      // populated at the same threshold or the warning is blank.
      message: remaining <= 2
        ? `Warning: Only ${remaining} attempt(s) remaining before temporary lockout.`
        : undefined
    };
  }

  /**
   * Record a successful operation (can be used to reset or reduce penalty)
   */
  recordSuccess(identifier: string, operation: RateLimitOperation): void {
    const key = `${operation}:${identifier.toLowerCase()}`;
    
    // Remove the rate limit entry on successful operation
    this.attempts.delete(key);
  }

  /**
   * Get current status for an identifier and operation
   */
  getStatus(
    identifier: string, 
    operation: RateLimitOperation
  ): RateLimitResult {
    const config = this.configs[operation];
    const key = `${operation}:${identifier.toLowerCase()}`;
    const now = Date.now();
    
    const attempt = this.attempts.get(key);

    if (!attempt) {
      return {
        allowed: true,
        attemptsRemaining: config.maxAttempts
      };
    }

    // Check if window has expired
    if (now - attempt.firstAttempt > config.windowMs) {
      return {
        allowed: true,
        attemptsRemaining: config.maxAttempts
      };
    }

    // Check if in block period
    if (attempt.count >= config.maxAttempts) {
      const timeSinceLastAttempt = now - attempt.lastAttempt;
      
      if (timeSinceLastAttempt < config.blockDurationMs) {
        const resetTime = attempt.lastAttempt + config.blockDurationMs;
        const remainingMs = resetTime - now;
        const remainingMinutes = Math.ceil(remainingMs / (60 * 1000));
        
        return {
          allowed: false,
          attemptsRemaining: 0,
          resetTime,
          message: `Blocked for ${remainingMinutes} more minute(s).`
        };
      } else {
        return {
          allowed: true,
          attemptsRemaining: config.maxAttempts
        };
      }
    }

    return {
      allowed: true,
      attemptsRemaining: config.maxAttempts - attempt.count
    };
  }

  /**
   * Clean up old attempts that are outside their windows
   */
  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, attempt] of Array.from(this.attempts.entries())) {
      const operation = key.split(':')[0] as keyof typeof this.configs;
      const config = this.configs[operation];
      
      // Remove if outside both the window and block duration
      const maxAge = Math.max(config.windowMs, config.blockDurationMs);
      
      if (now - attempt.lastAttempt > maxAge) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.attempts.delete(key));
    
    // Log cleanup stats in development
    if (process.env.NODE_ENV === 'development' && keysToDelete.length > 0) {
      debugLog(`Rate limit cleanup: removed ${keysToDelete.length} expired entries`);
    }
  }

  /**
   * Get statistics for monitoring
   */
  getStats(): {
    totalActiveEntries: number;
    entriesByOperation: Record<string, number>;
    blockedEntries: number;
  } {
    const stats = {
      totalActiveEntries: this.attempts.size,
      entriesByOperation: {} as Record<string, number>,
      blockedEntries: 0
    };

    const now = Date.now();

    for (const [key, attempt] of Array.from(this.attempts.entries())) {
      const operation = key.split(':')[0];
      const config = this.configs[operation as keyof typeof this.configs];
      
      // Count by operation
      stats.entriesByOperation[operation] = (stats.entriesByOperation[operation] || 0) + 1;
      
      // Count blocked entries
      if (attempt.count >= config.maxAttempts) {
        const timeSinceLastAttempt = now - attempt.lastAttempt;
        if (timeSinceLastAttempt < config.blockDurationMs) {
          stats.blockedEntries++;
        }
      }
    }

    return stats;
  }

  /**
   * Clear all rate limit data (use with caution)
   */
  clear(): void {
    this.attempts.clear();
  }

  /**
   * Destroy the rate limiter and clean up resources
   */
  destroy(): void {
    clearInterval(this.cleanupInterval);
    this.attempts.clear();
  }
}

// Singleton instance
export const rateLimitManager = new RateLimitManager();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    rateLimitManager.destroy();
  });
}